import { z } from 'zod';
import { exhibitorCoreSchema, exhibitorStatusSchema } from '../core';

// 2026 年度の申込スキーマ。2025 から大幅にシンプル化:
// - 机/椅子: tableCount / chairCount の単一カウント (机椅子セット概念は撤廃)
// - 電源: powerWatts (100W 単位の int, 0 = 不要、上限 1000W)
// - 0.5 ブース運用: 撤廃 (boothSizePreference / isHalfBooth は y2025 のみ)
// - 少額スポンサー: sponsorUnits (1 口 5,000 円、複数口可) + sponsorDisplayName (公開名)
//   sponsorUnits > 0 のとき sponsorDisplayName は必須。表示は申込時 + 支払い直前の
//   2 タイミングで提示し、支払い完了で確定 (Stripe 別 line item)。
//   宣伝目的の URL や長文は禁止 (UI 側でも警告するが zod でも reject)。
//
//   【重要】exhibitor_application_2026 の sponsorUnits / sponsorDisplayName は
//   「申込時に入力された仮の値」であって、支援の実績ではない。支払い直前まで何度でも
//   変更でき、支払わなければ何も確定しない。確定データは決済完了時に作られる
//   sponsorships テーブルの行 (kind = bundled / addon / standalone) が唯一の正であり、
//   実績の集計・公開名の掲載・金額の表示はすべて sponsorships 側を参照すること。
//   特に addon (後追いの追加支援) はこの仮値を一切更新しないため、仮値を実績として
//   扱うと addon 分がまるごと欠落する。
export const exhibitorApplication2026Schema = exhibitorCoreSchema
  .extend({
    tableCount: z.number().int().min(0).max(2),
    chairCount: z.number().int().min(0).max(4),
    powerWatts: z.number().int().min(0).max(1000).multipleOf(100),
    sponsorUnits: z.number().int().min(0).default(0),
    sponsorDisplayName: z
      .string()
      .max(30, '表示名は 30 文字以内で入力してください')
      .refine(
        (v) => !v || !/https?:\/\//i.test(v),
        '表示名に URL は含められません'
      )
      .optional()
      .nullable(),
  })
  .superRefine(requireSponsorDisplayName);

// 口数 1 以上なら表示名必須。admin 編集スキーマは .innerType() で superRefine が剥がれるため、
// このチェックを関数として切り出し、両方の schema に必ず付け直す。
// (表示名が空のまま支払いまで到達すると、公開されるスポンサー名が決まらない)
function requireSponsorDisplayName(
  data: { sponsorUnits: number; sponsorDisplayName?: string | null },
  ctx: z.RefinementCtx
) {
  if (data.sponsorUnits > 0 && !data.sponsorDisplayName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['sponsorDisplayName'],
      message: '少額スポンサーの表示名を入力してください',
    });
  }
}

export const exhibitorAdminFullUpdate2026Schema = exhibitorApplication2026Schema
  .innerType()
  .omit({ agreedToTerms: true, agreedToSafetyGuidelines: true })
  .extend({ status: exhibitorStatusSchema.optional() })
  .superRefine(requireSponsorDisplayName);

export type ExhibitorApplication2026Input = z.infer<typeof exhibitorApplication2026Schema>;
export type ExhibitorAdminFullUpdate2026Input = z.infer<typeof exhibitorAdminFullUpdate2026Schema>;

// スポンサー 1 口の金額 (円)。料金計算と支払い line item で共通利用。
export const SPONSOR_UNIT_AMOUNT_JPY = 5000;

// 口数 → 円換算
export function calculateSponsorAmount(units: number): number {
  return Math.max(0, units) * SPONSOR_UNIT_AMOUNT_JPY;
}

// 少額スポンサーの口数の上限。単独 (ゲスト) / 追加申込で共通利用。
// 出展申込フォーム側 (sponsorUnits) と支払い直前更新 (sponsorUpdateSchema) も同じ 20 を上限にしている。
export const SPONSOR_MAX_UNITS = 20;

// 公開表示名 (30 字以内・URL 禁止)。単独/追加申込で必須なので min(1) を課す。
// 出展申込フォーム側 (exhibitorApplication2026Schema.sponsorDisplayName) は口数 0 を許すため
// optional/nullable のまま据え置き、こちらは「申し込む = 口数 1 以上」専用として別に定義する。
export const sponsorDisplayNameSchema = z
  .string()
  .trim()
  .min(1, '表示名を入力してください')
  .max(30, '表示名は 30 文字以内で入力してください')
  .refine((v) => !/https?:\/\//i.test(v), '表示名に URL は含められません')
  // HTML/メールへの埋め込みでのインジェクション防止のため、< > を禁止する。
  .refine((v) => !/[<>]/.test(v), '表示名に使用できない文字 (< >) が含まれています');

// パターンA: 単独 (ゲスト) 申込。email は受領メール送信先として必須。
// 金額はサーバ側の固定単価で算出するため、クライアントからは units / displayName / email のみ受け取る。
export const standaloneSponsorshipSchema = z.object({
  units: z.number().int().min(1).max(SPONSOR_MAX_UNITS),
  displayName: sponsorDisplayNameSchema,
  email: z.string().email('メールアドレスの形式が正しくありません'),
});

// パターンB: 出展者の後追い追加申込。email はサーバが exhibitor から取得するため受け取らない。
export const addonSponsorshipSchema = z.object({
  units: z.number().int().min(1).max(SPONSOR_MAX_UNITS),
  displayName: sponsorDisplayNameSchema,
});

export type StandaloneSponsorshipInput = z.infer<typeof standaloneSponsorshipSchema>;
export type AddonSponsorshipInput = z.infer<typeof addonSponsorshipSchema>;
